import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const insuranceProviders = [
  { name: "Private Pay", icon: "ri-wallet-3-line" },
  { name: "Medicaid", icon: "ri-government-line" },
  { name: "Aetna", icon: "ri-shield-check-line" },
  { name: "Humana", icon: "ri-heart-pulse-line" },
  { name: "BCBS", icon: "ri-hospital-line" },
  { name: "Molina", icon: "ri-first-aid-kit-line" },
  { name: "Meridian", icon: "ri-building-2-line" },
  { name: "CountyCare", icon: "ri-community-line" },
];

const peopleServed = [
  { label: "Ages 18–65", description: "Adults needing daily living assistance, recovery support, or companionship due to disability or health conditions.", icon: "ri-user-line" },
  { label: "Ages 65–100+", description: "Senior care, companionship, and personal assistance for the elderly to maintain independence and dignity at home.", icon: "ri-parent-line" },
];

const shiftCategories = [
  { title: "Live-In", description: "24-hour rotation with a dedicated caregiver staying in the home, providing around-the-clock support.", icon: "ri-home-5-line" },
  { title: "12-Hour Shift", description: "Day or night 12-hour coverage for clients who need continuous supervision and assistance.", icon: "ri-sun-line" },
  { title: "5-Hour Minimum", description: "Part-time care starting from a 5-hour minimum visit, ideal for families who need flexible, shorter support sessions.", icon: "ri-time-line" },
];

const billingFaqs = [
  {
    q: "How is pricing determined?",
    a: "Pricing is based on the type of care, number of hours, and level of support needed. After your free assessment, we provide a clear, itemized quote with no hidden fees.",
  },
  {
    q: "Are there long-term contracts?",
    a: "No. We believe families should stay with us because they are satisfied. Our care plans operate on a simple agreement you can pause, modify, or discontinue at any time.",
  },
  {
    q: "Is there a minimum number of hours?",
    a: "Yes. Our minimum visit is 5 hours. We work with your schedule and budget to find the right balance for your family.",
  },
  {
    q: "Do you offer discounts for longer-term care?",
    a: "Yes. Families who commit to regular weekly schedules often benefit from preferred rates. Contact us to discuss your specific situation.",
  },
  {
    q: "What insurance plans do you accept?",
    a: "We accept Private Pay, Medicaid, Aetna, Humana, Blue Cross Blue Shield (BCBS), Molina, Meridian, and CountyCare. Our team can help verify your benefits.",
  },
];

export default function Pricing() {
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const observerRefs = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("data-animate-id");
          if (id && entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    observerRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const setRef = (id: string) => (el: HTMLElement | null) => {
    if (el) observerRefs.current.set(id, el);
  };

  const animateClass = (id: string, base = "") =>
    `transition-all duration-700 ease-out ${base} ${
      isVisible[id] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
    }`;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });
    fetch(form.action, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: data.toString(),
    })
      .then(() => setFormStatus("success"))
      .catch(() => setFormStatus("error"));
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden bg-brand-green-800">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20abstract%20green%20and%20beige%20organic%20shapes%20pattern%2C%20elegant%20background%20with%20warm%20neutral%20tones%2C%20calming%20professional%20atmosphere%2C%20minimal%20elegant%20design%2C%20no%20text%2C%20gentle%20gradients&width=1600&height=500&seq=pricinghero&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-300 mb-3 block">
            Transparent Pricing
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Pricing & Billing
          </h1>
          <p className="text-sm md:text-base text-brand-green-200 max-w-2xl mx-auto leading-relaxed">
            Flexible, transparent care options designed to fit your family's needs and budget. No hidden fees, no long-term contracts.
          </p>
        </div>
      </section>

      {/* Intro Banner */}
      <section className="py-8 md:py-10 bg-brand-green-50 border-b border-brand-green-100">
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
            <div>
              <h2 className="text-base md:text-lg font-semibold text-brand-green-800 mb-1">
                Pricing is customized to your care needs
              </h2>
              <p className="text-sm text-brand-green-700">
                Every family is different. Contact us for a free assessment and personalized quote — no obligation.
              </p>
            </div>
            <a
              href="tel:+17736997894"
              className="shrink-0 inline-flex items-center gap-2 px-6 py-3 bg-brand-green-600 text-white font-semibold text-sm rounded-full hover:bg-brand-green-700 transition-all whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-xs" />
              </div>
              Call 773-699-7894
            </a>
          </div>
        </div>
      </section>

      {/* Services Category by Hours */}
      <section className="py-14 md:py-20 bg-white" data-animate-id="shifts" ref={setRef("shifts")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("shifts", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Flexible Scheduling
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Services Category by Hours
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              We offer flexible care options designed around your schedule and your loved one's needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {shiftCategories.map((shift, i) => (
              <div
                key={shift.title}
                className={`bg-soft-gray rounded-xl p-6 md:p-8 border border-gray-100 hover:border-brand-green-200 hover:-translate-y-1 transition-all duration-300 text-center ${
                  isVisible["shifts"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-14 h-14 rounded-full bg-brand-green-50 flex items-center justify-center mx-auto mb-4">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className={`${shift.icon} text-2xl text-brand-green-600`} />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{shift.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{shift.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Area */}
      <section className="py-14 md:py-20 bg-soft-gray" data-animate-id="coverage" ref={setRef("coverage")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("coverage", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Service Areas
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Coverage Area
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              Our services cover the following Illinois counties.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 max-w-4xl mx-auto">
            {["Cook", "Kane", "Will", "DuPage", "LaSalle", "Kendall"].map((county, i) => (
              <div
                key={county}
                className={`flex items-center gap-2.5 px-4 py-3.5 rounded-xl bg-white border border-gray-100 hover:border-brand-green-200 hover:shadow-sm transition-all duration-300 ${
                  isVisible["coverage"] ? "opacity-100 scale-100" : "opacity-0 scale-90"
                }`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-map-pin-fill text-brand-green-600 text-sm" />
                </div>
                <span className="text-sm font-medium text-gray-900">{county}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* People We Serve */}
      <section className="py-14 md:py-20 bg-white" data-animate-id="people" ref={setRef("people")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("people", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Who We Care For
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              People We Serve
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              Sycamore Oasis provides compassionate care to individuals across a wide age range.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-3xl mx-auto">
            {peopleServed.map((group, i) => (
              <div
                key={group.label}
                className={`flex items-start gap-4 bg-soft-gray rounded-xl p-6 border border-gray-100 hover:border-brand-green-200 transition-all duration-300 ${
                  isVisible["people"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-brand-green-50 flex items-center justify-center shrink-0">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${group.icon} text-xl text-brand-green-600`} />
                  </div>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">{group.label}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{group.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Accepted */}
      <section className="py-14 md:py-20 bg-brand-green-50" data-animate-id="insurance" ref={setRef("insurance")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("insurance", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Payment Options
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Insurance We Accept
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              We work with multiple payment sources to make quality home care accessible for every family.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto">
            {insuranceProviders.map((ins, i) => (
              <div
                key={ins.name}
                className={`bg-white rounded-xl p-4 md:p-5 border border-gray-100 hover:border-brand-green-200 hover:-translate-y-1 transition-all duration-300 text-center ${
                  isVisible["insurance"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 rounded-lg bg-brand-green-50 flex items-center justify-center mx-auto mb-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${ins.icon} text-lg text-brand-green-600`} />
                  </div>
                </div>
                <p className="text-sm font-semibold text-gray-900">{ins.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Billing FAQ */}
      <section className="py-14 md:py-20 bg-white" data-animate-id="billing-faq" ref={setRef("billing-faq")}>
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto">
          <div className={animateClass("billing-faq", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Billing Questions
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Common Billing Questions
            </h2>
          </div>

          <div className={animateClass("billing-faq", "space-y-3")}>
            {billingFaqs.map((faq, idx) => (
              <div
                key={faq.q}
                className="bg-soft-gray rounded-xl border border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-semibold text-gray-900">{faq.q}</span>
                  <div className="w-6 h-6 flex items-center justify-center shrink-0">
                    <i
                      className={`ri-arrow-down-s-line text-lg text-brand-green-600 transition-transform duration-300 ${
                        openFaq === idx ? "rotate-180" : ""
                      }`}
                    />
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === idx ? "max-h-40" : "max-h-0"
                  }`}
                >
                  <p className="text-sm text-gray-500 leading-relaxed px-5 md:px-6 pb-5 md:pb-6">
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Get a Quote Form */}
      <section className="py-14 md:py-20 bg-soft-gray" data-animate-id="quote" ref={setRef("quote")}>
        <div className="w-full px-4 md:px-8 max-w-3xl mx-auto">
          <div className={animateClass("quote", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Get a Quote
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Request a Personalized Quote
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              Tell us a bit about your care needs and we will get back to you with a customized pricing estimate within one business day.
            </p>
          </div>

          <div className={animateClass("quote", "bg-white rounded-2xl p-6 md:p-8 border border-gray-100")}>
            {formStatus === "success" ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-brand-green-100 flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-brand-green-600" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Request Received!</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Thank you for reaching out. A care advisor will contact you with a personalized quote within one business day.
                </p>
                <button
                  onClick={() => setFormStatus("idle")}
                  className="px-6 py-2.5 bg-brand-green-600 text-white font-semibold text-sm rounded-full hover:bg-brand-green-700 transition-all"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form
                action="https://readdy.ai/api/form/d7vj2nvitsa7ugmsdi20"
                method="POST"
                data-readdy-form
                id="pricing-inquiry-form"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-xs font-medium text-gray-700 mb-1.5">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-xs font-medium text-gray-700 mb-1.5">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1.5">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs font-medium text-gray-700 mb-1.5">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                    placeholder="773-699-7894"
                  />
                </div>

                <div>
                  <label htmlFor="careType" className="block text-xs font-medium text-gray-700 mb-1.5">
                    Type of Care Needed *
                  </label>
                  <select
                    id="careType"
                    name="careType"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all bg-white"
                  >
                    <option value="">Select care type...</option>
                    <option value="homemaker">Homemaker Services</option>
                    <option value="companion">Home Care Companion</option>
                    <option value="personal-care">Personal Care Attendant</option>
                    <option value="dementia">Dementia & Parkinson's Support</option>
                    <option value="private-duty">Private Duty</option>
                    <option value="hospices">Hospices</option>
                    <option value="not-sure">Not Sure — Need Guidance</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="hoursPerWeek" className="block text-xs font-medium text-gray-700 mb-1.5">
                    Estimated Hours Per Week
                  </label>
                  <select
                    id="hoursPerWeek"
                    name="hoursPerWeek"
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all bg-white"
                  >
                    <option value="">Select hours...</option>
                    <option value="1-10">1 – 10 hours/week</option>
                    <option value="10-20">10 – 20 hours/week</option>
                    <option value="20-40">20 – 40 hours/week</option>
                    <option value="40+">40+ hours/week (full-time)</option>
                    <option value="live-in">Live-in (24/7)</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-medium text-gray-700 mb-1.5">
                    Additional Details (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all resize-none"
                    placeholder="Tell us more about your care needs, location, or any specific requirements..."
                  />
                  <p className="text-[11px] text-gray-400 mt-1">Maximum 500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === "submitting"}
                  className="w-full px-6 py-3 bg-brand-green-600 hover:bg-brand-green-700 disabled:bg-brand-green-400 text-white font-semibold rounded-lg transition-all duration-200 whitespace-nowrap"
                >
                  {formStatus === "submitting" ? "Sending..." : "Request My Quote"}
                </button>

                {formStatus === "error" && (
                  <p className="text-sm text-red-600 text-center">
                    Something went wrong. Please try again or call us directly.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-brand-green-800">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <div className="w-12 h-12 rounded-full bg-brand-green-700 flex items-center justify-center mx-auto mb-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-heart-3-line text-2xl text-brand-green-300" />
            </div>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-sm md:text-base text-brand-green-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            Contact us today for a free, no-obligation care assessment. We will help you find the right plan at the right price.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Link
              to="/care-plans"
              className="w-full sm:w-auto px-8 py-3.5 bg-white text-brand-green-800 font-semibold rounded-full hover:bg-brand-green-50 transition-all duration-200 text-center whitespace-nowrap"
            >
              View Care Plans
            </Link>
            <a
              href="tel:+17736997894"
              className="w-full sm:w-auto px-8 py-3.5 bg-brand-green-700 text-white font-semibold rounded-full hover:bg-brand-green-600 transition-all duration-200 text-center whitespace-nowrap"
            >
              Call 773-699-7894 (24/7)
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}